import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicForm, submitPublicForm } from '../utils/api';
import { CheckCircle2, AlertCircle, Loader2, Scale } from 'lucide-react';

export default function PublicFormPage() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState('');

  // Form data
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const data = await getPublicForm(token);
        setForm(data);
        // Init responses
        const init = {};
        (data.fields || []).forEach(f => {
          if (f.type === 'checkbox') init[f.id] = false;
          else if (f.type === 'checkboxes') init[f.id] = [];
          else init[f.id] = '';
        });
        setResponses(init);
      } catch {
        setError('This form is not available or is no longer accepting submissions.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const validate = () => {
    const errs = {};
    if (form.collect_name) {
      if (!firstName.trim()) errs.firstName = 'First name is required';
      if (!lastName.trim()) errs.lastName = 'Last name is required';
    }
    if (form.collect_email && !email.trim()) errs.email = 'Email is required';
    (form.fields || []).forEach(f => {
      if (f.required) {
        const val = responses[f.id];
        if (f.type === 'checkbox' && !val) errs[f.id] = 'This field is required';
        else if (f.type === 'checkboxes' && (!val || val.length === 0)) errs[f.id] = 'Select at least one option';
        else if (typeof val === 'string' && !val.trim()) errs[f.id] = 'This field is required';
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const result = await submitPublicForm(token, { first_name: firstName, last_name: lastName, email, phone, responses });
      setConfirmMsg(result.message || 'Thank you! Your submission has been received.');
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const updateResponse = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) setErrors(prev => { const e = { ...prev }; delete e[fieldId]; return e; });
  };

  const toggleCheckbox = (fieldId, option) => {
    setResponses(prev => {
      const arr = Array.isArray(prev[fieldId]) ? [...prev[fieldId]] : [];
      const idx = arr.indexOf(option);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(option);
      return { ...prev, [fieldId]: arr };
    });
  };

  // ── Styles ──
  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    padding: '40px 20px',
    fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
  };
  const containerStyle = {
    maxWidth: '640px', margin: '0 auto',
  };
  const cardStyle = {
    background: '#1e293b', border: '1px solid #334155', borderRadius: '12px',
    padding: '32px', marginBottom: '16px', color: '#e2e8f0',
  };
  const labelStyle = {
    display: 'block', fontSize: '0.88rem', fontWeight: 500, color: '#cbd5e1',
    marginBottom: '6px',
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', fontSize: '0.92rem', borderRadius: '8px',
    border: '1px solid #475569', background: '#0f172a', color: '#e2e8f0',
    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s',
  };
  const errorStyle = { fontSize: '0.78rem', color: '#f87171', marginTop: '4px' };
  const requiredStar = <span style={{ color: '#f87171', marginLeft: '2px' }}>*</span>;

  // ── Loading / Error states ──
  if (loading) return (
    <div style={pageStyle}>
      <div style={{ textAlign: 'center', paddingTop: '120px' }}>
        <Loader2 size={32} style={{ color: '#d4a853', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  if (error && !form) return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 32px' }}>
          <AlertCircle size={40} style={{ color: '#f87171', marginBottom: '12px' }} />
          <h2 style={{ fontSize: '1.2rem', color: '#f1f5f9', marginBottom: '8px' }}>Form Unavailable</h2>
          <p style={{ fontSize: '0.92rem', color: '#94a3b8' }}>{error}</p>
        </div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 32px' }}>
          <CheckCircle2 size={48} style={{ color: '#22c55e', marginBottom: '16px' }} />
          <h2 style={{ fontSize: '1.3rem', color: '#f1f5f9', marginBottom: '10px' }}>Submission Received</h2>
          <p style={{ fontSize: '0.95rem', color: '#94a3b8', lineHeight: 1.6 }}>{confirmMsg}</p>
        </div>
        {/* Branding */}
        <div style={{ textAlign: 'center', marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <Scale size={14} style={{ color: '#d4a853' }} />
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Powered by ZeusLaw</span>
        </div>
      </div>
    </div>
  );

  // ── Render Field ──
  const renderField = (field) => {
    const hasError = errors[field.id];

    switch (field.type) {
      case 'section_heading':
        return (
          <div style={{ marginTop: '8px', paddingBottom: '6px', borderBottom: '1px solid #334155' }}>
            <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#d4a853', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{field.label}</div>
          </div>
        );
      case 'text':
      case 'phone':
      case 'email_field':
      case 'number':
        return (
          <div>
            <label style={labelStyle}>{field.label}{field.required && requiredStar}</label>
            <input
              type={field.type === 'number' ? 'number' : field.type === 'email_field' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
              style={{ ...inputStyle, borderColor: hasError ? '#f87171' : '#475569' }}
              placeholder={field.placeholder || ''}
              value={responses[field.id] || ''}
              onChange={(e) => updateResponse(field.id, e.target.value)}
            />
            {hasError && <div style={errorStyle}>{hasError}</div>}
          </div>
        );
      case 'paragraph':
        return (
          <div>
            <label style={labelStyle}>{field.label}{field.required && requiredStar}</label>
            <textarea
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
              placeholder={field.placeholder || ''}
              value={responses[field.id] || ''}
              onChange={(e) => updateResponse(field.id, e.target.value)}
            />
            {hasError && <div style={errorStyle}>{hasError}</div>}
          </div>
        );
      case 'date':
        return (
          <div>
            <label style={labelStyle}>{field.label}{field.required && requiredStar}</label>
            <input type="date" style={{ ...inputStyle, borderColor: hasError ? '#f87171' : '#475569' }}
              value={responses[field.id] || ''} onChange={(e) => updateResponse(field.id, e.target.value)} />
            {hasError && <div style={errorStyle}>{hasError}</div>}
          </div>
        );
      case 'dropdown':
        return (
          <div>
            <label style={labelStyle}>{field.label}{field.required && requiredStar}</label>
            <select style={{ ...inputStyle, borderColor: hasError ? '#f87171' : '#475569' }}
              value={responses[field.id] || ''} onChange={(e) => updateResponse(field.id, e.target.value)}>
              <option value="">Select...</option>
              {(field.options || []).filter(o => o.trim()).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            {hasError && <div style={errorStyle}>{hasError}</div>}
          </div>
        );
      case 'checkbox':
        return (
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.92rem', color: '#e2e8f0', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!responses[field.id]}
                onChange={(e) => updateResponse(field.id, e.target.checked)}
                style={{ accentColor: '#d4a853', width: '16px', height: '16px' }} />
              {field.label}{field.required && requiredStar}
            </label>
            {hasError && <div style={errorStyle}>{hasError}</div>}
          </div>
        );
      case 'checkboxes':
        return (
          <div>
            <label style={labelStyle}>{field.label}{field.required && requiredStar}</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
              {(field.options || []).filter(o => o.trim()).map(o => (
                <label key={o} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.88rem', color: '#cbd5e1', cursor: 'pointer' }}>
                  <input type="checkbox"
                    checked={(responses[field.id] || []).includes(o)}
                    onChange={() => toggleCheckbox(field.id, o)}
                    style={{ accentColor: '#d4a853', width: '15px', height: '15px' }} />
                  {o}
                </label>
              ))}
            </div>
            {hasError && <div style={errorStyle}>{hasError}</div>}
          </div>
        );
      case 'address':
        return (
          <div>
            <label style={labelStyle}>{field.label || 'Address'}{field.required && requiredStar}</label>
            <textarea rows={2} style={{ ...inputStyle, resize: 'vertical' }}
              placeholder={field.placeholder || 'Street address, City, State ZIP'}
              value={responses[field.id] || ''} onChange={(e) => updateResponse(field.id, e.target.value)} />
            {hasError && <div style={errorStyle}>{hasError}</div>}
          </div>
        );
      case 'signature':
        return (
          <div>
            <label style={labelStyle}>{field.label || 'Signature'}{field.required && requiredStar}</label>
            <input type="text" style={{ ...inputStyle, fontFamily: "'Brush Script MT', cursive", fontSize: '1.2rem', borderColor: hasError ? '#f87171' : '#475569' }}
              placeholder="Type your full name as signature"
              value={responses[field.id] || ''} onChange={(e) => updateResponse(field.id, e.target.value)} />
            {hasError && <div style={errorStyle}>{hasError}</div>}
          </div>
        );
      case 'file':
        return (
          <div>
            <label style={labelStyle}>{field.label || 'File Upload'}{field.required && requiredStar}</label>
            <div style={{ padding: '16px', border: '2px dashed #475569', borderRadius: '8px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
              File upload will be available after submission integration
            </div>
          </div>
        );
      default:
        return (
          <div>
            <label style={labelStyle}>{field.label}</label>
            <input type="text" style={inputStyle} value={responses[field.id] || ''}
              onChange={(e) => updateResponse(field.id, e.target.value)} />
          </div>
        );
    }
  };

  // ── Main Form ──
  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        {/* Org branding */}
        {form.organization?.name && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#d4a853', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{form.organization.name}</div>
          </div>
        )}

        {/* Form header card */}
        <div style={cardStyle}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 300, color: '#f1f5f9', marginBottom: '6px', fontFamily: "'Inter', sans-serif" }}>{form.name}</h1>
          {form.description && <p style={{ fontSize: '0.92rem', color: '#94a3b8', marginBottom: '16px' }}>{form.description}</p>}

          {/* Status banner */}
          <div style={{
            padding: '10px 14px', borderRadius: '8px',
            background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)',
            fontSize: '0.82rem', color: '#4ade80',
          }}>
             This form is publicly accessible and accepting submissions.
          </div>
        </div>

        {/* Form fields card */}
        <form onSubmit={handleSubmit}>
          <div style={cardStyle}>
            {/* Built-in name fields */}
            {form.collect_name && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>First Name{requiredStar}</label>
                  <input style={{ ...inputStyle, borderColor: errors.firstName ? '#f87171' : '#475569' }}
                    value={firstName} onChange={(e) => { setFirstName(e.target.value); setErrors(p => { const n = {...p}; delete n.firstName; return n; }); }}
                    placeholder="First name" />
                  {errors.firstName && <div style={errorStyle}>{errors.firstName}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Last Name{requiredStar}</label>
                  <input style={{ ...inputStyle, borderColor: errors.lastName ? '#f87171' : '#475569' }}
                    value={lastName} onChange={(e) => { setLastName(e.target.value); setErrors(p => { const n = {...p}; delete n.lastName; return n; }); }}
                    placeholder="Last name" />
                  {errors.lastName && <div style={errorStyle}>{errors.lastName}</div>}
                </div>
              </div>
            )}

            {/* Built-in email / phone */}
            {(form.collect_email || form.collect_phone) && (
              <div style={{ display: 'grid', gridTemplateColumns: form.collect_email && form.collect_phone ? '1fr 1fr' : '1fr', gap: '14px', marginBottom: '16px' }}>
                {form.collect_email && (
                  <div>
                    <label style={labelStyle}>Email{requiredStar}</label>
                    <input type="email" style={{ ...inputStyle, borderColor: errors.email ? '#f87171' : '#475569' }}
                      value={email} onChange={(e) => { setEmail(e.target.value); setErrors(p => { const n = {...p}; delete n.email; return n; }); }}
                      placeholder="email@example.com" />
                    {errors.email && <div style={errorStyle}>{errors.email}</div>}
                  </div>
                )}
                {form.collect_phone && (
                  <div>
                    <label style={labelStyle}>Phone</label>
                    <input type="tel" style={inputStyle} value={phone}
                      onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" />
                  </div>
                )}
              </div>
            )}

            {/* Dynamic fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {(form.fields || []).map(field => (
                <div key={field.id}>{renderField(field)}</div>
              ))}
            </div>

            {/* Error summary */}
            {error && (
              <div style={{ marginTop: '16px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', fontSize: '0.85rem' }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%', marginTop: '24px', padding: '12px 24px',
                background: submitting ? '#475569' : 'linear-gradient(135deg, #d4a853, #b8902e)',
                color: '#0f172a', border: 'none', borderRadius: '8px',
                fontSize: '0.95rem', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>

        {/* Footer branding */}
        <div style={{ textAlign: 'center', marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <Scale size={14} style={{ color: '#d4a853' }} />
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Powered by ZeusLaw</span>
        </div>
      </div>
    </div>
  );
}
